import { constTrue, O, pipe, RA, t, TE, URLFromString } from '@packages/prelude'
import * as domain from '@packages/domain'
import { PreviewData } from '~/hooks/useIndiAudioPreview'
import { nonBlank } from '~/commons/string'

type Segment = domain.AudioSegment.Type

const selectedNameOf = (id: number, initialData: O.Option<domain.Api.IALoadPreviewResponse.Type>) =>
  pipe(initialData, O.chain(domain.Api.IALoadPreviewResponse.parameterById(String(id)).getOption), O.chain(nonBlank))

const previewFileUrlOf = (id: number, initialData: O.Option<domain.Api.IALoadPreviewResponse.Type>) =>
  pipe(
    initialData,
    O.chain(d => domain.Api.IALoadPreviewResponse.fileById(String(id)).asFold().headOption(d))
  )

const templateNameOf = (fieldId: string, initialData: O.Option<domain.Api.IALoadPreviewResponse.Type>) =>
  pipe(initialData, O.chain(domain.Api.IALoadPreviewResponse.templateNameById(fieldId)), O.chain(nonBlank))

// Selectors for the two "asset" arms (userUpload, aiGreeting); the name arms carry neither url.
export const objectUrlOf: (seg: Segment) => O.Option<string> = domain.AudioSegment.ADT.matchStrict({
  userUpload: s => s.objectUrl,
  aiGreeting: s => s.objectUrl,
  preRecorded: () => O.none,
  templateName: () => O.none
})

export const remoteUrlOf: (seg: Segment) => O.Option<string> = domain.AudioSegment.ADT.matchStrict({
  userUpload: s => s.remoteUrl,
  aiGreeting: s => s.remoteUrl,
  preRecorded: () => O.none,
  templateName: () => O.none
})

export const buildSegments = (
  meta: domain.Meta.ProductPersonalization.Type,
  parameters: ReadonlyArray<domain.IAParameter.Type>,
  initialData: O.Option<domain.Api.IALoadPreviewResponse.Type>
): ReadonlyArray<Segment> => {
  // The parameter a field binds to, narrowed to the arm its kind requires; a wrong-arm binding
  // is dropped like an absent one.
  const boundParam =
    <P extends domain.IAParameter.Type>(isParam: (p: domain.IAParameter.Type) => p is P) =>
    (field: { parameterId: number }): O.Option<P> =>
      pipe(parameters, domain.IAParameters.byId(field.parameterId), O.filter(isParam))

  const nameSegment = (p: domain.Parameter.Person.Type, field: { id: string; label: string }): Segment => ({
    kind: 'preRecorded',
    id: p.id,
    fieldId: field.id,
    title: field.label,
    description: p.description,
    optional: p.optional,
    selectedName: selectedNameOf(p.id, initialData),
    previewUrl: O.none
  })

  const assetCommon = (p: domain.Parameter.UserAudio.Type, field: { id: string; label: string }) => ({
    id: p.id,
    fieldId: field.id,
    title: field.label,
    description: p.description,
    optional: p.optional,
    audio: O.none as O.Option<Blob>,
    objectUrl: O.none as O.Option<string>,
    remoteUrl: previewFileUrlOf(p.id, initialData)
  })

  const templateNameSegment = (field: { id: string; label: string; description: string }): Segment => ({
    kind: 'templateName',
    fieldId: field.id,
    title: field.label,
    description: field.description,
    optional: true,
    name: templateNameOf(field.id, initialData)
  })

  // Only the audio kinds concern this module; matching the `Audio` sub-ADT keeps the match
  // exhaustive over them (a new audio kind is a compile error here) without naming the rest.
  return pipe(
    meta.fields,
    RA.filter(domain.Meta.Fields.PersonalizationField.isAudio),
    RA.filterMap(
      domain.Meta.Fields.PersonalizationField.Audio.matchStrict<O.Option<Segment>>({
        preRecorded: f =>
          pipe(
            f,
            boundParam(domain.IAParameter.ADT.is.PE),
            O.map(p => nameSegment(p, f))
          ),
        userUpload: f =>
          pipe(
            f,
            boundParam(domain.IAParameter.ADT.is.UO),
            O.map(p => ({
              ...assetCommon(p, f),
              kind: 'userUpload' as const,
              maxDuration: p['max-duration'],
              video: O.fromEither(URLFromString.decode(f.videoUrl))
            }))
          ),
        aiGreeting: f =>
          pipe(
            f,
            boundParam(domain.IAParameter.ADT.is.UO),
            O.map(p => ({ ...assetCommon(p, f), kind: 'aiGreeting' as const, text: '' }))
          ),
        templateName: f => O.some(templateNameSegment(f))
      })
    )
  )
}

export type NameSegment = domain.AudioSegment.PreRecorded | domain.AudioSegment.TemplateName
export type AssetSegment = domain.AudioSegment.AiGreeting | domain.AudioSegment.UserUpload

// Type guard for the asset arms (they carry audio/objectUrl/remoteUrl + an IA param id).
export const isAsset = domain.AudioSegment.ADT.isAnyOf(['userUpload', 'aiGreeting'])

const nameADT = domain.AudioSegment.ADT.select(['preRecorded', 'templateName'])
const assetADT = domain.AudioSegment.ADT.select(['userUpload', 'aiGreeting'])

// Optional onto the name value wherever it lives in the union (selectedName vs name), via the
// domain-derived per-arm lenses.
const nameValue = nameADT.matchOptional<O.Option<t.NonEmptyString>>({
  preRecorded: domain.AudioSegment.PreRecordedLens.selectedName.asOptional(),
  templateName: domain.AudioSegment.TemplateNameLens.name.asOptional()
})

// Optional onto an asset arm's remoteUrl (userUpload vs aiGreeting), via the domain-derived lenses.
const assetRemoteUrl = assetADT.matchOptional<O.Option<string>>({
  userUpload: domain.AudioSegment.UserUploadLens.remoteUrl.asOptional(),
  aiGreeting: domain.AudioSegment.AiGreetingLens.remoteUrl.asOptional()
})

// Replace the segment sharing this fieldId; every other segment passes through unchanged.
const replaceByFieldId = (fieldId: string, updated: Segment): ((segs: ReadonlyArray<Segment>) => ReadonlyArray<Segment>) =>
  RA.map((s: Segment): Segment => (s.fieldId === fieldId ? updated : s))

// The caller holds the narrow arm (it lives inside an AudioSegment.ADT.matchStrict branch), so a
// wrong-kind call is a *compile* error. `nameValue` focuses the (differently-named) value per arm.
export const setName = (seg: NameSegment, name: O.Option<t.NonEmptyString>) =>
  replaceByFieldId(seg.fieldId, nameValue.set(name)(seg))

export const setRecording = (seg: AssetSegment, audio: Blob, objectUrl: string) =>
  replaceByFieldId(seg.fieldId, { ...seg, audio: O.some(audio), objectUrl: O.some(objectUrl) })

export const setGreetingText = (seg: domain.AudioSegment.AiGreeting, text: string) =>
  replaceByFieldId(seg.fieldId, domain.AudioSegment.AiGreetingLens.text.set(text)(seg))

export const discard = (seg: AssetSegment) => replaceByFieldId(seg.fieldId, { ...seg, audio: O.none, objectUrl: O.none })

// Addressed by IA *param id* (the preview response's files are param-id-keyed); only the asset
// arms carry one.
export const setRemoteUrl = (id: number, url: string): ((segs: ReadonlyArray<Segment>) => ReadonlyArray<Segment>) =>
  RA.map((s: Segment): Segment => (isAsset(s) && s.id === id ? assetRemoteUrl.set(O.some(url))(s) : s))

const pendingGreeting = (s: Segment): s is domain.AudioSegment.AiGreeting =>
  domain.AudioSegment.ADT.is.aiGreeting(s) && O.isNone(s.audio) && O.isSome(nonBlank(s.text))

export const greetingsToSynthesize: (segments: ReadonlyArray<Segment>) => ReadonlyArray<domain.AudioSegment.AiGreeting> =
  RA.filter(pendingGreeting)

export const synthesizeMissingGreetings =
  (generate: (text: string) => TE.TaskEither<domain.Error.Type, Blob>) =>
  (segments: ReadonlyArray<Segment>): TE.TaskEither<domain.Error.Type, ReadonlyArray<Segment>> =>
    pipe(
      greetingsToSynthesize(segments),
      RA.reduce(TE.right<domain.Error.Type, ReadonlyArray<Segment>>(segments), (acc, seg) =>
        pipe(
          acc,
          TE.chain(segs =>
            pipe(
              generate(seg.text.trim()),
              TE.chainIOK(blob => () => setRecording(seg, blob, URL.createObjectURL(blob))(segs))
            )
          )
        )
      )
    )

export const toPreviewData = (segments: ReadonlyArray<Segment>): ReadonlyArray<PreviewData> =>
  pipe(
    segments,
    RA.filterMap(
      domain.AudioSegment.ADT.matchStrict({
        preRecorded: (s): O.Option<PreviewData> =>
          pipe(
            s.selectedName,
            O.map(name => PreviewData.of.Name({ id: s.id, name }))
          ),
        templateName: (s): O.Option<PreviewData> =>
          pipe(
            s.name,
            O.map(name => PreviewData.of.TemplateName({ id: s.fieldId, name }))
          ),
        userUpload: (s): O.Option<PreviewData> =>
          pipe(
            s.audio,
            O.map(audio => PreviewData.of.Recording({ id: s.id, audio }))
          ),
        aiGreeting: (s): O.Option<PreviewData> =>
          pipe(
            s.audio,
            O.map(audio => PreviewData.of.AIGreeting({ id: s.id, audio }))
          )
      })
    )
  )

// Only AUDIO inputs gate orderability; name (preRecorded) and template-name inputs never
// block the button.
const audioPresent = domain.AudioSegment.ADT.matchStrict({
  preRecorded: constTrue,
  templateName: constTrue,
  userUpload: (s): boolean => s.optional || O.isSome(s.audio),
  aiGreeting: (s): boolean => s.optional || O.isSome(s.audio) || O.isSome(nonBlank(s.text))
})

export const allRequiredAudioPresent = (segments: ReadonlyArray<Segment>): boolean => pipe(segments, RA.every(audioPresent))
